<script setup>
import PassengersAgeSelector from './PassengersAgeSelector.vue';
import PassengersCounter from './PassengersCounter.vue';
import { lreplace } from '@js/utils/toolbox';
import { usePassengerCounter } from './usePassengerCounter';

const tooManyPassengersText = lreplace('searchBox.passengers.exeedingLimit.1company', {
	company: '<a href="https://www.ticket.se" target="_blank">Ticket</a> <a href="tel:0770-457523">0770 45 75 23</a>'
});

const passengers = defineModel('passengers', {
	required: true,
	type: Object,
	default: () => ({})
});

const emit = defineEmits(['changeView']);

// Adults counter
const adults = usePassengerCounter(passengers, 'adults', {
	minCount: 1,
	maxCount: 9,
	maxTotal: 9,
	includeAges: false
});

// Children counter
const children = usePassengerCounter(passengers, 'child', {
	minCount: 0,
	maxCount: 9,
	maxTotal: 9
});

const isIncrementDisabled = computed(() =>
	adults.isIncrementDisabled.value && children.isIncrementDisabled.value
);

const childAgeOptions = [0, ...Array.from({ length: 16 }, (_, i) => i + 2)];
</script>

<template>
  <div class="abPassengers__view abPassengers__view--default selected" role="menu">
    <!-- Adults Section -->
    <div class="abPassengers__group abPassengers__group--adult">
      <PassengersCounter
        :label="$l('searchBox.passengers.adults')"
        :age-range="`18+ ${$l('searchBox.passengers.years')}`"
        :count="passengers.adults"
        :is-increment-disabled="adults.isIncrementDisabled.value"
        :is-decrement-disabled="adults.isDecrementDisabled.value"
        @increment="adults.increment"
        @decrement="adults.decrement"
      />
      <div class="abPassengers__wrapper">
        <div
          class="abPassengers__msg"
          :class="{ hidden: !isIncrementDisabled }"
          target="_blank"
          v-html="tooManyPassengersText"
        />
      </div>
    </div>

    <!-- Children Section -->
    <div class="abPassengers__group abPassengers__group--child">
      <PassengersCounter
        :label="$l('searchBox.passengers.children')"
        :age-range="`0-17 ${$l('searchBox.passengers.years')}`"
        :count="passengers.child.count"
        :is-increment-disabled="children.isIncrementDisabled.value"
        :is-decrement-disabled="children.isDecrementDisabled.value"
        @increment="children.increment"
        @decrement="children.decrement"
      />
    </div>

    <!-- Child Ages Section -->
    <PassengersAgeSelector
      class="abPassengers__group--child"
      type="child"
      :ages="passengers.child.ages"
      :active-index="children.activeAgeIndex.value"
      :age-options="childAgeOptions"
      :label="$l('searchBox.passengers.child')"
      :select-age-prompt="$l('searchBox.passengers.selectChildrensAge')"
      :show-count="passengers.child.count > 0"
      @remove-age="children.removeAge"
      @set-active-index="children.setActiveAgeIndex"
      @set-age="children.setAge"
    >
      <template #age-display="{ age }">
        <span v-if="age === 0">0-23 {{ $l('searchBox.passengers.months') }}</span>
        <span v-else>{{ age }} {{ $l('searchBox.passengers.yearsOld') }}</span>
      </template>

      <template #age-option="{ age }">
        <template v-if="age === 0">
          <span class="abPassengers__infantMonths">0-23</span>
          <span class="abPassengers__infant">{{ $l('global.simplePhrases.infant') }}</span>
          <span>{{ $l('searchBox.passengers.months') }}</span>
        </template>
        <template v-else>
          <span>{{ age }}</span>
          <span>{{ $l('searchBox.passengers.yearsOld') }}</span>
        </template>
      </template>
    </PassengersAgeSelector>

    <!-- Youth Change View -->
    <div
      v-if="passengers.isYouthTicketEnabled"
      class="abPassengers__group abPassengers__group--changeView"
    >
      <button
        role="menuitem"
        class="abPassengers__item abBtn abBtn abBtn--whiteBlue"
        tabindex="-1"
        data-view="youth"
        @click.stop="emit('changeView')"
      >
        <span class="abPassengers__type">
          {{ $l('searchBox.passengers.youth') }} (12-25 {{ $l('searchBox.passengers.yearsOld') }})
        </span>
        <span class="icon icon-chevron-right" />
      </button>
      <div class="abPassengers__wrapper">
        <div class="abPassengers__msg">
          {{ $l('searchBox.passengers.youthWarning.1') }} {{ $l('searchBox.passengers.youthWarning.2') }}
        </div>
      </div>
    </div>
  </div>
</template>
